import { usePlatform } from '@localagent/platform';
import { toast } from '@localagent/ui/components/sonner';
import type { WorkspaceProjection } from '@localagent/sdk';
import {
  useVersionHistory,
  useUnsavedChanges,
  useRestoreVersion,
  useWorkspaceRootPaths,
} from '../../hooks/useWorkspaceHistory';
import { useSessions } from '../../hooks/useSessions';
import { useWorkspaceProjection } from '../../hooks/useWorkspaces';
import { VersionHistoryPanel } from './VersionHistoryPanel';

interface WorkspaceCloudDetailProps {
  workspaceId: string;
}

export function WorkspaceCloudDetail({ workspaceId }: WorkspaceCloudDetailProps) {
  const { projection } = useWorkspaceProjection(workspaceId);
  if (!projection || projection.assignment.routing_class !== 'cloud') return null;
  return <WorkspaceCloudDetailForProjection projection={projection} />;
}

export function WorkspaceCloudDetailForProjection({
  projection,
}: {
  projection: WorkspaceProjection;
}) {
  if (projection.assignment.routing_class !== 'cloud') return null;
  return <CloudWorkspaceDetail projection={projection} />;
}

function CloudWorkspaceDetail({ projection }: { projection: WorkspaceProjection }) {
  const workspaceId = projection.workspace_id;
  const platform = usePlatform();

  // Version history. The history/unsaved reads are
  // session-scoped (the backend resolves the workspace pod through the
  // session), so the panel follows the workspace's most recent session. With no
  // session yet there is nothing to read — the panel says so rather than
  // rendering a silently empty timeline.
  const { data: sessions } = useSessions(workspaceId);
  const versioningSessionId = [...(sessions ?? [])].sort((a, b) =>
    (b.created_at ?? '').localeCompare(a.created_at ?? '')
  )[0]?.id;

  const versionHistory = useVersionHistory(workspaceId, versioningSessionId);
  const unsavedChanges = useUnsavedChanges(workspaceId, versioningSessionId);
  const versionHistoryEntries = versionHistory.data?.entries ?? [];
  const rootPaths = useWorkspaceRootPaths(workspaceId);
  const restoreVersion = useRestoreVersion(workspaceId);

  if (platform.type !== 'desktop') {
    return null;
  }

  const versionHistoryAvailable = projection.capabilities.version_history;

  return (
    <div className="space-y-4" data-testid="workspace-version-history-section">
      <h2 className="text-title-2 font-semibold">Version history</h2>

      {/* Version history. Only a
          provisioned repository has versions to show. */}
      {versionHistoryAvailable && versioningSessionId && (
        <VersionHistoryPanel
          entries={versionHistoryEntries}
          isLoading={versionHistory.isLoading}
          error={versionHistory.error ? versionHistory.error.message : null}
          unsavedCount={unsavedChanges.data ?? 0}
          availablePaths={rootPaths.data ?? []}
          isRestoring={restoreVersion.isPending}
          onRestore={(version, paths) => {
            restoreVersion.mutate(
              {
                sessionId: versioningSessionId,
                version,
                ...(paths !== undefined ? { paths } : {}),
              },
              {
                onSuccess: () => toast.success('Workspace restored to the selected version'),
                onError: (err) => toast.error(`Restore failed: ${err.message}`),
              }
            );
          }}
        />
      )}

      {versionHistoryAvailable && !versioningSessionId && (
        <div className="rounded-lg bg-card p-4" data-testid="version-history-no-session">
          <h3 className="text-label font-semibold">Version history</h3>
          <p className="mt-2 text-body text-muted-foreground">
            Start a session in this workspace to browse and restore its saved versions.
          </p>
        </div>
      )}
    </div>
  );
}
